import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stack, EnvelopeSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("password");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const formatDetail = (detail) => {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
    return "";
  };

  const passwordLogin = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/password/login`, { email, password }, { withCredentials: true });
      setUser(r.data);
      navigate("/console", { state: { user: r.data } });
    } catch (e) {
      setError(formatDetail(e.response?.data?.detail) || "Sign-in failed.");
    }
    setBusy(false);
  };

  const requestOtp = async () => {
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/otp/request`, { email }, { withCredentials: true });
      setStage("code");
    } catch (e) {
      setError(formatDetail(e.response?.data?.detail) || "Could not send code.");
    }
    setBusy(false);
  };

  const verifyOtp = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/auth/otp/verify`, { email, code }, { withCredentials: true });
      setUser(r.data);
      navigate("/console", { state: { user: r.data } });
    } catch (e) {
      setError(formatDetail(e.response?.data?.detail) || "Verification failed.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Stack size={26} weight="fill" className="text-slate-900" />
          <span className="font-heading font-extrabold text-2xl tracking-tight">MAI MATRIX<span className="align-super text-[10px]">™</span></span>
        </div>
        <p className="text-center text-xs text-slate-400 uppercase tracking-widest mb-8">Global Market Access Intelligence Platform</p>

        <div className="bg-white border border-slate-200 p-6 space-y-4">
          {stage === "password" ? (
            <div className="space-y-3">
              <Input data-testid="login-email-input" type="email" placeholder="you@company.com" value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-none h-11" />
              <Input data-testid="login-password-input" type="password" placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="rounded-none h-11"
                onKeyDown={(e) => e.key === "Enter" && email && password && passwordLogin()} />
              <Button data-testid="password-login-btn" onClick={passwordLogin} disabled={!email || !password || busy}
                className="w-full bg-slate-900 text-white hover:bg-slate-700 rounded-none h-11">
                {busy ? "Signing in…" : "Sign in with password"}
              </Button>
              <button data-testid="switch-to-otp-btn" onClick={() => { setStage("email"); setError(""); }}
                className="w-full text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1.5">
                <EnvelopeSimple size={13} /> Email me a login code instead
              </button>
            </div>
          ) : stage === "email" ? (
            <div className="space-y-3">
              <Input data-testid="otp-email-input" type="email" placeholder="you@company.com" value={email}
                onChange={(e) => setEmail(e.target.value)} className="rounded-none h-11"
                onKeyDown={(e) => e.key === "Enter" && email && requestOtp()} />
              <Button data-testid="otp-request-btn" onClick={requestOtp} disabled={!email || busy}
                variant="outline" className="w-full rounded-none gap-2 h-11 border-slate-300">
                <EnvelopeSimple size={16} /> {busy ? "Sending…" : "Send login code"}
              </Button>
              <button data-testid="switch-to-password-btn" onClick={() => { setStage("password"); setError(""); }}
                className="w-full text-xs text-slate-400 hover:text-slate-600">Use password instead</button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Code sent to <span className="font-semibold text-slate-800">{email}</span></p>
              <Input data-testid="otp-code-input" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="rounded-none h-11 text-center tracking-[0.5em] font-mono-data text-lg"
                onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verifyOtp()} />
              <Button data-testid="otp-verify-btn" onClick={verifyOtp} disabled={code.length !== 6 || busy}
                className="w-full bg-slate-900 text-white hover:bg-slate-700 rounded-none h-11">
                {busy ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button data-testid="otp-back-btn" onClick={() => { setStage("email"); setCode(""); setError(""); }}
                className="w-full text-xs text-slate-400 hover:text-slate-600">Use a different email</button>
            </div>
          )}

          {error && <p data-testid="login-error" className="text-xs text-red-600 bg-red-50 border border-red-200 p-2.5">{error}</p>}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-4">Access is restricted to administrator-approved accounts.</p>
      </div>
    </div>
  );
}
